import { FRONTDOOR_UPLOAD_WAIT_TIMEOUT_MILLISECONDS, NETWORK_PING_INTERVAL } from '../../AppConstants';
import { RetryServiceCaller, RetryServiceCallUntilSuccessOrTimeout } from '../utils/Utils';
import { LogServiceEvent } from '../TelemetryLogger/LogServiceEvent';
import { LogEventNames } from '../interfaces/ITelemetryLogger';
import { FrontDoorFileUploadError, FrontDoorFileUploadErrors } from './FrontDoorFileUploadErrors';
import { LogError } from '../../utils/ErrorTelemetry';
import { CancelableWait, isNullOrUndefined } from '../../utils/utils';
import { GetFrontDoorBaseUrl } from '../office-js/HostAppSettings';
import { GetNetworkConnectionMonitorInstance, InternetState, IsConnectedToNetwork, NetworkConnectionListener, NetworkPingStatusListener } from '../../frontend/NetworkConnectionMonitor';
import { TranscriptionHistoryDump } from '../utils/TranscriptionHistoryDump';

export type ConversationId = string;

export type OnUploadProgressCallback = (percentDone: number) => void;

export type FrontDoorUploadResult = { conversationId: ConversationId, audioLengthInMS: number, speechFDUrl: string, correlationId?: string };

function ShouldRetryFrontDoorUploadError(e: any) {
    if (!(e instanceof FrontDoorFileUploadError))
        return false;

    return (e.name == FrontDoorFileUploadErrors.NetworkError || e.name == FrontDoorFileUploadErrors.InternalServerError);
}

function ThrowFrontDoorUploadErrorStatus(statusCode: number, apiName : string) {
    switch (statusCode) {
        case 400:
            throw new FrontDoorFileUploadError(FrontDoorFileUploadErrors.UnsupportedFileFormat, 'File format is not supported by FrontDoor');
        case 404:
            throw new FrontDoorFileUploadError(FrontDoorFileUploadErrors.NetworkError, `${apiName} returned ${statusCode} status code`);
        case 500:
            throw new FrontDoorFileUploadError(FrontDoorFileUploadErrors.InternalServerError, `${apiName} returned ${statusCode} status code`);
    }

    throw new Error("Analyze uploaded audio returned non-successful status code " + statusCode);
}

export enum FrontDoorFileUploadState {
  FRONT_DOOR_FILE_UPLOAD_SERVICE_PAUSED = 'FRONT_DOOR_FILE_UPLOAD_SERVICE_PAUSED',
  FRONT_DOOR_FILE_UPLOAD_SERVICE_RESUMED = 'FRONT_DOOR_FILE_UPLOAD_SERVICE_RESUMED'
}

export class FrontDoorFileUpload implements NetworkConnectionListener, NetworkPingStatusListener{
    private xhr: XMLHttpRequest | null = null;
    private analyseAudioRetryObj: any;
    private frontDoorFileUploadState: FrontDoorFileUploadState = FrontDoorFileUploadState.FRONT_DOOR_FILE_UPLOAD_SERVICE_RESUMED;
    private frontDoorResumeCallback: Function | null = null;
    private lastSuccessPingResponseTime = 0;
    private frontDoorHistory: TranscriptionHistoryDump | null = null;

    constructor(transcriptionHistoryDump: TranscriptionHistoryDump) {
      this.frontDoorHistory = transcriptionHistoryDump;
      GetNetworkConnectionMonitorInstance().addListener(this);
      GetNetworkConnectionMonitorInstance().StartPingServiceForListener(this);
    }

    public async AnalyzeAudio(sessionId: string, fileDownloadUrl: string, fileContentType: string,
      onUploadProgress: OnUploadProgressCallback, onUploadDone: () => void, locale: string, corelationId?: string): Promise<FrontDoorUploadResult> {
        const logServiceEvent = new LogServiceEvent(LogEventNames.ServiceFrontdoorUpload);
        logServiceEvent.Start();
        let retryCounter = 0;
        this.analyseAudioRetryObj = RetryServiceCallUntilSuccessOrTimeout(logServiceEvent, async () => {
          return new Promise(async (resolve, reject) => {
              if (retryCounter++ > 0) {
                await this.EnsureFrontDoorState();
              }
              try {
                const response = await this.AnalyzeAudioFromDownloadUrl(sessionId, fileDownloadUrl, fileContentType, onUploadProgress, onUploadDone, locale, corelationId);
                GetNetworkConnectionMonitorInstance().StopPingServiceForListener(this);
                GetNetworkConnectionMonitorInstance().removeListener(this);
                resolve(response);
              }catch(e) {
                reject(e);
              }
          })
         }, false /*shouldRetryOnNull*/, RetryServiceCaller.AnalyzeAudioFromDownloadUrl, ShouldRetryFrontDoorUploadError, () => true /*shouldIgnoreTimeout*/)
        return this.analyseAudioRetryObj.promise;
    }

    onInternetConnectionStatusChanged(state: InternetState): void {
      this.frontDoorHistory?.AddEventToTranscriptionHistory('Front Door received onInternetConnectionStatusChanged to '+state);
      switch(state) {
        case InternetState.INTERNET_CONNECTED:
          this.updateState(FrontDoorFileUploadState.FRONT_DOOR_FILE_UPLOAD_SERVICE_RESUMED);
          break;
        case InternetState.INTERNET_DISCONNECTED:
          this.updateState(FrontDoorFileUploadState.FRONT_DOOR_FILE_UPLOAD_SERVICE_PAUSED);
          break;
      }
    }

    onPingServiceStatusUpdate(state: InternetState): void {
      switch(state) {
        case InternetState.PING_RESPONSE_SUCCESS:
          const currentTime = new Date().getTime() ;
          const timeGapFromLastSuccessResponse = currentTime - this.lastSuccessPingResponseTime;
          if (timeGapFromLastSuccessResponse > NETWORK_PING_INTERVAL + 10000 /* Buffer to avoid small delays*/ && !isNullOrUndefined(this.analyseAudioRetryObj)) {
            this.frontDoorHistory?.AddEventToTranscriptionHistory('Network PING received as SUCCESS after long wait, continuing to next attempt.');
            this.analyseAudioRetryObj.continueToNextAttempt();
          }
          this.lastSuccessPingResponseTime = currentTime;
          this.updateState(FrontDoorFileUploadState.FRONT_DOOR_FILE_UPLOAD_SERVICE_RESUMED);
          break;
        case InternetState.PING_RESPONSE_FAIL:
          this.updateState(FrontDoorFileUploadState.FRONT_DOOR_FILE_UPLOAD_SERVICE_PAUSED);
          break;
      }
    }

    public updateState(frontDoorFileUploadNewState: FrontDoorFileUploadState) {
      if (this.frontDoorFileUploadState == frontDoorFileUploadNewState) {
        return;
      }
      this.frontDoorHistory?.AddEventToTranscriptionHistory(frontDoorFileUploadNewState);
      switch(frontDoorFileUploadNewState) {
        case FrontDoorFileUploadState.FRONT_DOOR_FILE_UPLOAD_SERVICE_RESUMED:
          this.frontDoorFileUploadState = frontDoorFileUploadNewState;
          this.analyseAudioRetryObj.continueToNextAttempt();
          if (!isNullOrUndefined(this.frontDoorResumeCallback)) {
            this.frontDoorResumeCallback!();
          }
          break;
        case FrontDoorFileUploadState.FRONT_DOOR_FILE_UPLOAD_SERVICE_PAUSED:
          this.frontDoorFileUploadState = frontDoorFileUploadNewState;
          break;
      }
    }

    private async EnsureFrontDoorState() {
      if (this.frontDoorFileUploadState == FrontDoorFileUploadState.FRONT_DOOR_FILE_UPLOAD_SERVICE_PAUSED) {
        await this.waitForFrontDoorUploadToResume();
      }
      else if (!IsConnectedToNetwork() || !await GetNetworkConnectionMonitorInstance().isPingConnected()) {
        this.updateState(FrontDoorFileUploadState.FRONT_DOOR_FILE_UPLOAD_SERVICE_PAUSED);
        await this.waitForFrontDoorUploadToResume();
      }
      Promise.resolve();
    }

    private waitForFrontDoorUploadToResume() {
      return new Promise<void>((resolve, _) => {
        const onFrontDoorUploadResumedCallback = () => {
          this.frontDoorResumeCallback = null;
          resolve();

        };

        this.frontDoorResumeCallback = onFrontDoorUploadResumedCallback;
      })
    }

    public Cancel() {
        GetNetworkConnectionMonitorInstance().removeListener(this);
        GetNetworkConnectionMonitorInstance().StopPingServiceForListener(this);
        if (this.xhr != null) {
            this.xhr.abort();
        }
        if  (this.analyseAudioRetryObj) {
             this.analyseAudioRetryObj.cancel();
        }
    }

    private async AnalyzeAudioFromDownloadUrl(sessionId: string, fileDownloadUrl: string, fileContentType: string, onUploadProgress: OnUploadProgressCallback, onUploadDone: () => void, locale: string, correlationId?: string): Promise<FrontDoorUploadResult> {
      const formData = new FormData();
      formData.append('userSessionId', sessionId);
      formData.append('app', 'Word');
      formData.append('sourceLanguage', locale);
      formData.append('scenarioName', 'WordTranscription');
      formData.append('client', 'web');
      formData.append('speechMode', 'ConversationTranscriptionAsync');
      formData.append('File-DownloadUrl', fileDownloadUrl);
      formData.append('File-ContentType', fileContentType)

      return new Promise<FrontDoorUploadResult>((resolve, reject) => {
          if (this.xhr != null) {
              this.xhr.abort();
          }

          this.xhr = new XMLHttpRequest();

          let frontDoorResponseWaitObj: any = null;

          // Callback called when there's some progress uploading the file to FrontDoor.
          this.xhr.upload.onprogress = (event: ProgressEvent<EventTarget>) => {
              onUploadProgress(Math.min(event.loaded / event.total, 1));
          }

          // Callback called when the file has been uploaded to FrontDoor, now we just need to wait for a response with the transcript.
          this.xhr.upload.onload = () => {
              // The transcription process takes some time, so give FrontDoor 30 minutes to respond.
              frontDoorResponseWaitObj = CancelableWait(FRONTDOOR_UPLOAD_WAIT_TIMEOUT_MILLISECONDS);

              frontDoorResponseWaitObj.promise.then(() => {
                  this.Cancel();
                  frontDoorResponseWaitObj = null;
                  reject(new FrontDoorFileUploadError(FrontDoorFileUploadErrors.WaitTimeoutReached, `Waited for AnalyzeAudioFromDownloadUrl for ${FRONTDOOR_UPLOAD_WAIT_TIMEOUT_MILLISECONDS} ms but didn't receive a response`));
              });

              onUploadDone();
          };

          try {
              let analyzeAudioFromDownloadUrl = GetFrontDoorBaseUrl() + "?action=AnalyzeAudioFromDownloadUrl";
              if (correlationId != null) {
                analyzeAudioFromDownloadUrl = analyzeAudioFromDownloadUrl + `&cid=${correlationId}`;
              }
              this.xhr.open('POST', analyzeAudioFromDownloadUrl);
              this.xhr.send(formData);
          } catch (e: any) {
              reject(new FrontDoorFileUploadError(FrontDoorFileUploadErrors.NetworkError, `Failed opening or sending XHR with error ${e.message}`));
          } finally {
            if (!isNullOrUndefined(frontDoorResponseWaitObj)) {
              frontDoorResponseWaitObj.cancel();
              frontDoorResponseWaitObj = null;
            }
          }

          // Callback called on XHR abort
          this.xhr.onabort = () => {
            if (!isNullOrUndefined(frontDoorResponseWaitObj)) {
              frontDoorResponseWaitObj.cancel();
              frontDoorResponseWaitObj = null;
            }
          }

          // Callback called on XHR error
          this.xhr.onerror = (_event: ProgressEvent<EventTarget>) => {

              if (!isNullOrUndefined(frontDoorResponseWaitObj)) {
                frontDoorResponseWaitObj.cancel();
                frontDoorResponseWaitObj = null;
              }

              const xhrStatus = this.xhr == null ? 'xhr is null' : 'xhr.status = ' + this.xhr.status;
              reject(new FrontDoorFileUploadError(FrontDoorFileUploadErrors.NetworkError, `AnalyzeAudioFromDownloadUrl request onerror callback was called. ${xhrStatus}`));
          }

          // Callback called when we have received a response from FrontDoor.
          this.xhr.onload = async () => {
              try {
                  if (this.xhr == null)
                     throw new Error("xhr is null during onload");

                  if (this.xhr.status != 200) {
                      ThrowFrontDoorUploadErrorStatus(this.xhr.status, 'AnalyzeAudioFromDownloadUrl', /* FdApiname*/);
                  }

                  let jsonResponse = null;

                  try {
                      jsonResponse = JSON.parse(this.xhr.responseText);
                  } catch (e) {
                      LogError(LogEventNames.Warning, `Could not parse AnalyzeAudioFromDownloadUrl response. status: ${this.xhr.status} type: ${this.xhr.responseType} text: ${this.xhr.responseText}`)
                      throw e;
                  }

                  ValidateAnalyzeUploadedAudioJsonResponse(jsonResponse);
                  resolve({ conversationId: jsonResponse.conversationId, audioLengthInMS: jsonResponse.audioLengthInMS, speechFDUrl: jsonResponse.speechFDUrl, correlationId: correlationId });
              } catch (e) {
                  reject(e);
              } finally {
                if (!isNullOrUndefined(frontDoorResponseWaitObj)) {
                  frontDoorResponseWaitObj.cancel();
                  frontDoorResponseWaitObj = null;
                }
              }
          }
      });
  }
}

function ValidateAnalyzeUploadedAudioJsonResponse(jsonResponse: any) {
    if (jsonResponse == null)
        throw new Error("jsonResponse is null");

    if (jsonResponse.conversationId == null)
        throw new Error("Didn't get conversationId");

    if (typeof jsonResponse.conversationId != 'string')
        throw new Error("conversationId is not a string");

    if (jsonResponse.audioLengthInMS == null)
        throw new Error("Didn't get audioLengthInMS");

    if (typeof jsonResponse.audioLengthInMS != 'number')
        throw new Error("audioLengthInMS is not a number");

    if (jsonResponse.speechFDUrl == null)
        throw new Error("Didn't get speechFDUrl");

    if (typeof jsonResponse.speechFDUrl != 'string')
        throw new Error("fdSpeechURL is not a string");
}